/** Shared registration complete flow persistence (user-classic + user-modern). */

export interface RegisterCompleteFlowState {
  phoneNumber: string
  sessionId: string
  smsVerified: boolean
  provider?: string
  firebaseUid?: string
  method?: string
}

export interface ModernRegisterCompleteFlowState {
  fromOtp?: boolean
  fromPasscode?: boolean
  phone?: string
  sessionId?: string
  username?: string
  password?: string
}

type StoredRegisterCompletePayload = {
  savedAt: number
  phoneNumber?: string
  smsVerified?: boolean
  fromOtp?: boolean
  fromPasscode?: boolean
  phone?: string
  password?: string
  username?: string
  sessionId?: string
  provider?: string
  firebaseUid?: string
  method?: string
}

const COMPLETE_STATE_STORAGE_KEY = 'huay-register-complete-flow-state'
const COMPLETE_STATE_TTL_MS = 15 * 60 * 1000

function writeStoredPayload(payload: Omit<StoredRegisterCompletePayload, 'savedAt'>) {
  try {
    sessionStorage.setItem(
      COMPLETE_STATE_STORAGE_KEY,
      JSON.stringify({ ...payload, savedAt: Date.now() })
    )
  } catch {
    // Ignore storage failures.
  }
}

function readStoredPayload(): StoredRegisterCompletePayload | null {
  try {
    const raw = sessionStorage.getItem(COMPLETE_STATE_STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as StoredRegisterCompletePayload
    if (!parsed?.sessionId || !parsed?.savedAt) {
      return null
    }

    if (Date.now() - parsed.savedAt > COMPLETE_STATE_TTL_MS) {
      clearRegisterCompleteFlowState()
      return null
    }

    return parsed
  } catch {
    return null
  }
}

export function persistRegisterCompleteFlowState(state: RegisterCompleteFlowState) {
  writeStoredPayload({
    phoneNumber: state.phoneNumber,
    sessionId: state.sessionId,
    smsVerified: state.smsVerified === true,
    provider: state.provider,
    firebaseUid: state.firebaseUid,
    method: state.method,
  })
}

export function readRegisterCompleteFlowState(): RegisterCompleteFlowState | null {
  const parsed = readStoredPayload()
  if (!parsed?.phoneNumber || !parsed.sessionId || parsed.smsVerified !== true) {
    return null
  }

  return {
    phoneNumber: parsed.phoneNumber,
    sessionId: parsed.sessionId,
    smsVerified: true,
    provider: parsed.provider,
    firebaseUid: parsed.firebaseUid,
    method: parsed.method,
  }
}

export function resolveRegisterCompleteFlowState(
  routeState: RegisterCompleteFlowState | null | undefined
): RegisterCompleteFlowState | null {
  if (routeState?.phoneNumber && routeState?.sessionId && routeState?.smsVerified) {
    return routeState
  }
  return readRegisterCompleteFlowState()
}

export function isValidModernRegisterCompleteFlowState(
  state: ModernRegisterCompleteFlowState | null | undefined
): state is ModernRegisterCompleteFlowState & { sessionId: string } {
  if (!state?.sessionId) return false
  if (state.fromPasscode === true) {
    return Boolean(state.phone && state.password)
  }
  if (state.fromOtp === true) {
    return Boolean(state.phone)
  }
  return false
}

export function persistModernRegisterCompleteFlowState(state: ModernRegisterCompleteFlowState) {
  if (!state.sessionId) return

  writeStoredPayload({
    fromOtp: state.fromOtp === true,
    fromPasscode: state.fromPasscode === true,
    phone: state.phone,
    sessionId: state.sessionId,
    username: state.username,
    password: state.password,
  })
}

export function readModernRegisterCompleteFlowState(): ModernRegisterCompleteFlowState | null {
  const parsed = readStoredPayload()
  if (!parsed?.sessionId) return null

  if (parsed.fromPasscode && parsed.phone && parsed.password) {
    return {
      fromPasscode: true,
      phone: parsed.phone,
      sessionId: parsed.sessionId,
      username: parsed.username,
      password: parsed.password,
    }
  }

  const phone = parsed.phone || parsed.phoneNumber
  if ((parsed.fromOtp || parsed.smsVerified) && phone) {
    return {
      fromOtp: true,
      phone,
      sessionId: parsed.sessionId,
      username: parsed.username,
    }
  }

  return null
}

export function resolveModernRegisterCompleteFlowState(
  routeState: ModernRegisterCompleteFlowState | null | undefined
): ModernRegisterCompleteFlowState | null {
  if (isValidModernRegisterCompleteFlowState(routeState)) {
    return routeState
  }
  return readModernRegisterCompleteFlowState()
}

export function clearRegisterCompleteFlowState() {
  try {
    sessionStorage.removeItem(COMPLETE_STATE_STORAGE_KEY)
  } catch {
    // Ignore storage cleanup failures.
  }
}
